import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Tooltip,
  TablePagination
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Source as SourceIcon,
  Business as BusinessIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon
} from '@mui/icons-material';
import { Source, SourceInput, Tenant } from '../types';
import sourceService from '../services/sourceService';
import tenantService from '../services/tenantService';
import { useUserRole } from '../hooks/useUserRole';

interface DialogState {
  open: boolean;
  mode: 'create' | 'edit';
  source?: Source;
}

const Sources: React.FC = () => {
  const [sources, setSources] = useState<Source[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogState, setDialogState] = useState<DialogState>({ open: false, mode: 'create' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  // User role check
  const { isAdmin: hasControlPanelAccess, isLoading: userLoading } = useUserRole();

  // Form state
  const [formData, setFormData] = useState<SourceInput>({
    sourceId: undefined,
    name: '',
    description: '',
    isActive: true
  });

  useEffect(() => {
    loadSources();
    loadTenants();
  }, []);

  const loadSources = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await sourceService.getSources();
      setSources(data || []);
    } catch (err: any) {
      console.error('Error loading sources:', err);
      setError(err.message || 'Failed to load sources');
    } finally {
      setLoading(false);
    }
  };

  const loadTenants = async () => {
    try {
      const data = await tenantService.getTenants();
      setTenants(data || []);
    } catch (err: any) {
      console.error('Error loading tenants:', err);
      // Don't set error for tenants loading failure
    }
  };

  const handleOpenDialog = (mode: 'create' | 'edit', source?: Source) => {
    if (mode === 'create') {
      setFormData({
        sourceId: undefined,
        name: '',
        description: '',
        isActive: true
      });
    } else if (source) {
      setFormData({
        sourceId: source.sourceId,
        name: source.name,
        description: source.description || '',
        isActive: source.isActive
      });
    }
    
    setDialogState({ open: true, mode, source });
  };

  const handleCloseDialog = () => {
    setDialogState({ open: false, mode: 'create' });
    setFormData({
      sourceId: undefined,
      name: '',
      description: '',
      isActive: true
    });
  };

  const handleSubmit = async () => {
    try {
      setError(null);
      
      if (!formData.name) {
        setError('Name is required');
        return;
      }
      
      if (dialogState.mode === 'create') {
        await sourceService.createSource(formData);
        setSuccess('Source created successfully!');
      } else if (dialogState.source) {
        await sourceService.updateSource(dialogState.source.id, formData);
        setSuccess('Source updated successfully!');
      }
      
      handleCloseDialog();
      await loadSources();
    } catch (err: any) {
      console.error('Error saving source:', err);
      setError(err.message || 'Failed to save source');
    }
  };

  const handleDelete = async (source: Source) => {
    if (!window.confirm(`Are you sure you want to delete "${source.name}"?`)) {
      return;
    }

    try {
      setError(null);
      await sourceService.deleteSource(source.id);
      setSuccess('Source deleted successfully!');
      await loadSources();
    } catch (err: any) {
      console.error('Error deleting source:', err);
      setError(err.message || 'Failed to delete source');
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusChip = (isActive: boolean) => {
    return isActive ? 
      <Chip label="Active" color="success" size="small" icon={<ActiveIcon />} /> : 
      <Chip label="Inactive" color="default" size="small" icon={<InactiveIcon />} />;
  };

  const filteredSources = sources.filter((source: Source) =>
    source.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    source.sourceId.toString().includes(searchTerm.toLowerCase()) ||
    (source.description && source.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const paginatedSources = filteredSources.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Clear success/error messages after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (userLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!hasControlPanelAccess) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You don't have permission to access this page. Control Panel access requires role ID ≤ 2.
        </Alert>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Sources
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage data sources in the PECS system
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog('create')}
          sx={{ borderRadius: 2 }}
        >
          Add Source
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    backgroundColor: 'primary.light',
                    color: 'primary.main',
                    mr: 2
                  }}
                >
                  <SourceIcon />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {sources.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Sources
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    backgroundColor: 'success.light',
                    color: 'success.main',
                    mr: 2
                  }}
                >
                  <ActiveIcon />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {sources.filter(s => s.isActive).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Sources
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    backgroundColor: 'info.light',
                    color: 'info.main',
                    mr: 2
                  }}
                >
                  <BusinessIcon />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {sources.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Sources
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    backgroundColor: 'warning.light',
                    color: 'warning.main',
                    mr: 2
                  }}
                >
                  <InactiveIcon />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {sources.filter(s => !s.isActive).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Inactive Sources
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search sources by code, name, or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ maxWidth: 400 }}
        />
      </Box>

      {/* Data Table */}
      <Paper elevation={2}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Created Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: 120 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : paginatedSources.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {searchTerm ? 'No sources found matching your search.' : 'No sources available.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedSources.map((source) => (
                  <TableRow key={source.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {source.sourceId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {source.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {source.description || 'No description'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getStatusChip(source.isActive)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(source.createdDate).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Edit Source">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog('edit', source)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Source">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(source)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {filteredSources.length > rowsPerPage && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredSources.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogState.open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogState.mode === 'create' ? 'Create New Source' : 'Edit Source'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Source ID"
              value={formData.sourceId || ''}
              onChange={(e) => setFormData({ ...formData, sourceId: Number(e.target.value) || undefined })}
              fullWidth
              disabled={dialogState.mode === 'edit'}
              helperText={dialogState.mode === 'create' ? 'Leave empty for auto-generation' : 'Cannot be changed'}
            />
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive ?? true}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 
             dialogState.mode === 'create' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Sources;
